// src/modules/myvault/views/AutomationRulesList.tsx

// What? Admin-only list view of event-triggered automation rules (R-3).
// Why? Gives admins a single place to see which rules exist, flip them
//      on/off, and jump into the execution log. Pairs with R-3a (user-
//      triggered action buttons) as the event-triggered counterpart.
// How? Lists rules from /api/workspace/myvault/automation/rules, resolves
//      table display names against useSchemas so the Table column shows
//      something meaningful, and exposes enabled + table filters.

import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Switch,
  Table,
  Tooltip,
  Typography,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import BoltIcon from "@mui/icons-material/Bolt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import {
  useAutomationRules,
  useAutomationRuleMutations,
  type AutomationRule,
  type TriggerType,
} from "../hooks/useAutomationRules";
import { useSchemas } from "../hooks";

const TRIGGER_LABELS: Record<TriggerType, string> = {
  ROW_CREATED: "Row created",
  FIELD_CHANGED: "Field changed",
  FIELD_MATCHES: "Field matches",
  BUTTON_CLICK: "Button click",
  SCHEDULED: "Scheduled",
};

function triggerSummary(rule: AutomationRule): string {
  const base = TRIGGER_LABELS[rule.trigger_type] || rule.trigger_type;
  const cfg = rule.trigger_config || {};
  const field = typeof cfg.field === "string" ? cfg.field : undefined;
  const value = cfg.value;
  if (rule.trigger_type === "FIELD_CHANGED" && field) {
    return `${base}: ${field}`;
  }
  if (rule.trigger_type === "FIELD_MATCHES" && field) {
    return value !== undefined && value !== null
      ? `${base}: ${field} = ${String(value)}`
      : `${base}: ${field}`;
  }
  if (rule.trigger_type === "SCHEDULED" && typeof cfg.cron === "string") {
    return `${base}: ${cfg.cron}`;
  }
  return base;
}

function formatRelative(iso: string | null): string {
  if (!iso) return "Never";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AutomationRulesList() {
  const navigate = useNavigate();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  const [tableFilter, setTableFilter] = useState<string>("");
  const [enabledFilter, setEnabledFilter] = useState<"all" | "on" | "off">(
    "all",
  );
  const [deleteTarget, setDeleteTarget] = useState<AutomationRule | null>(null);
  const [busyRuleId, setBusyRuleId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const filters = useMemo(
    () => ({
      tableId: tableFilter || undefined,
      enabled:
        enabledFilter === "on"
          ? true
          : enabledFilter === "off"
            ? false
            : undefined,
    }),
    [tableFilter, enabledFilter],
  );

  const { rules, loading, error, refresh } = useAutomationRules(
    workspaceId,
    filters,
  );
  const { schemas } = useSchemas(workspaceId);
  const { toggleRule, deleteRule } = useAutomationRuleMutations(workspaceId);

  const tableNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of schemas) {
      map.set(t.id, t.display_name || t.table_name || t.id);
    }
    return map;
  }, [schemas]);

  const handleToggle = async (rule: AutomationRule) => {
    setBusyRuleId(rule.id);
    setErrorMessage(null);
    try {
      await toggleRule(rule.id);
      await refresh();
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to toggle rule",
      );
    } finally {
      setBusyRuleId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusyRuleId(deleteTarget.id);
    setErrorMessage(null);
    try {
      await deleteRule(deleteTarget.id);
      setDeleteTarget(null);
      await refresh();
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to delete rule",
      );
    } finally {
      setBusyRuleId(null);
    }
  };

  if (!workspaceId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">Please select a workspace first.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Stack spacing={3}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          sx={{ gap: 2 }}
        >
          <Box>
            <Typography
              level="h2"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <BoltIcon sx={{ color: "primary.500" }} />
              Automations
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              Event-driven rules that fire when records are created, updated, or
              matched by condition.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<HistoryIcon />}
              onClick={() => navigate("executions")}
            >
              Execution log
            </Button>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<RefreshIcon />}
              onClick={() => refresh()}
            >
              Refresh
            </Button>
            <Button
              startDecorator={<AddIcon />}
              onClick={() => navigate("new")}
            >
              New rule
            </Button>
          </Stack>
        </Stack>

        <Card variant="outlined">
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ sm: "flex-end" }}
            >
              <FormControl sx={{ minWidth: 240 }}>
                <FormLabel>Target table</FormLabel>
                <Select
                  size="sm"
                  value={tableFilter}
                  onChange={(_, val) => setTableFilter((val as string) || "")}
                  placeholder="All tables"
                >
                  <Option value="">All tables</Option>
                  {schemas.map((t) => (
                    <Option key={t.id} value={t.id}>
                      {t.display_name || t.table_name}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <FormLabel>Status</FormLabel>
                <Select
                  size="sm"
                  value={enabledFilter}
                  onChange={(_, val) =>
                    setEnabledFilter((val as "all" | "on" | "off") || "all")
                  }
                >
                  <Option value="all">All rules</Option>
                  <Option value="on">Enabled</Option>
                  <Option value="off">Disabled</Option>
                </Select>
              </FormControl>
            </Stack>
          </CardContent>
        </Card>

        {error && <Alert color="danger">{error}</Alert>}
        {errorMessage && <Alert color="danger">{errorMessage}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : rules.length === 0 ? (
          <Sheet
            variant="soft"
            sx={{
              borderRadius: "md",
              p: 6,
              textAlign: "center",
            }}
          >
            <Typography level="title-md" sx={{ mb: 1 }}>
              No automation rules yet
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.tertiary", mb: 2 }}>
              Create a rule to run actions whenever records in a table change.
            </Typography>
            <Button
              startDecorator={<AddIcon />}
              onClick={() => navigate("new")}
            >
              Create your first rule
            </Button>
          </Sheet>
        ) : (
          <Sheet
            variant="outlined"
            sx={{ borderRadius: "sm", overflow: "hidden" }}
          >
            <Table size="sm" hoverRow stickyHeader>
              <thead>
                <tr>
                  <th style={{ width: "24%" }}>Name</th>
                  <th style={{ width: "18%" }}>Table</th>
                  <th style={{ width: "22%" }}>Trigger</th>
                  <th style={{ width: "10%" }}>Enabled</th>
                  <th style={{ width: "10%" }}>Runs</th>
                  <th style={{ width: "16%" }}>Last fired</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => {
                  const isBusy = busyRuleId === rule.id;
                  return (
                    <tr
                      key={rule.id}
                      style={{
                        cursor: "pointer",
                        opacity: isBusy ? 0.6 : 1,
                      }}
                      onClick={() => navigate(rule.id)}
                    >
                      <td>
                        <Typography level="body-sm" fontWeight="md">
                          {rule.name}
                        </Typography>
                        {rule.allow_cascading && (
                          <Chip size="sm" variant="soft" color="warning">
                            cascades
                          </Chip>
                        )}
                      </td>
                      <td>
                        <Typography level="body-sm">
                          {tableNameById.get(rule.table_id) || rule.table_id}
                        </Typography>
                      </td>
                      <td>
                        <Typography level="body-xs" noWrap>
                          {triggerSummary(rule)}
                        </Typography>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <Switch
                          size="sm"
                          checked={rule.enabled}
                          disabled={isBusy}
                          onChange={() => handleToggle(rule)}
                        />
                      </td>
                      <td>
                        <Tooltip
                          title="View executions for this rule"
                          variant="soft"
                        >
                          <Typography
                            level="body-sm"
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `executions?rule_id=${encodeURIComponent(rule.id)}`,
                              );
                            }}
                            sx={{
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            {rule.execution_count}
                          </Typography>
                        </Tooltip>
                      </td>
                      <td>
                        <Typography
                          level="body-xs"
                          sx={{ color: "text.tertiary" }}
                        >
                          {formatRelative(rule.last_executed_at)}
                        </Typography>
                      </td>
                      <td
                        onClick={(e) => e.stopPropagation()}
                        style={{ textAlign: "right" }}
                      >
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="sm"
                            variant="plain"
                            onClick={() => navigate(rule.id)}
                            aria-label={`Edit ${rule.name}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={() => setDeleteTarget(rule)}
                            aria-label={`Delete ${rule.name}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Sheet>
        )}
      </Stack>

      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <ModalClose />
          <Typography level="title-lg">Delete automation rule?</Typography>
          <Typography level="body-sm" sx={{ mt: 1 }}>
            This will delete <strong>{deleteTarget?.name}</strong> and its
            execution history. This cannot be undone.
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 2, justifyContent: "flex-end" }}
          >
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={handleDelete}
              loading={!!busyRuleId}
            >
              Delete
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
